import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Button, Chip, Heading, TextField } from '@lumx/react';
import {
  mdiArrowLeft, mdiChevronDown, mdiChevronRight, mdiClose,
  mdiLan, mdiEmailOutline, mdiMagnify, mdiMapMarker, mdiMonitor,
  mdiRocket, mdiUnfoldLessHorizontal, mdiUnfoldMoreHorizontal, mdiApplicationOutline,
} from '@mdi/js';
import MdiIcon from '@mdi/react';
import { INITIAL_NODES, MOCK_USERS } from '../data/mockData';
import { OUNode, User, TestRun, Tester } from '../types';

// ── Tree types & helpers ──────────────────────────────────────────────────────

interface SelectableNode {
  id: string;
  name: string;
  type: 'OrganizationalUnit' | 'Location';
  children: SelectableNode[];
}

function buildSelectableTree(nodes: OUNode[], parentId: string | null = null): SelectableNode[] {
  return nodes
    .filter(n => n.parentId === parentId)
    .map(n => ({ id: n.id, name: n.name, type: n.type, children: buildSelectableTree(nodes, n.id) }));
}

function allNodeIds(nodes: SelectableNode[]): string[] {
  return nodes.flatMap(n => [n.id, ...allNodeIds(n.children)]);
}

function descendantIds(node: SelectableNode): string[] {
  return [node.id, ...node.children.flatMap(c => descendantIds(c))];
}

function ancestorIds(id: string, flatNodes: OUNode[]): string[] {
  const node = flatNodes.find(n => n.id === id);
  if (!node || !node.parentId) return [];
  return [node.parentId, ...ancestorIds(node.parentId, flatNodes)];
}

function findNode(nodes: SelectableNode[], id: string): SelectableNode | null {
  for (const n of nodes) {
    if (n.id === id) return n;
    const found = findNode(n.children, id);
    if (found) return found;
  }
  return null;
}

function filterTree(nodes: SelectableNode[], query: string): SelectableNode[] {
  if (!query) return nodes;
  const q = query.toLowerCase();
  const filter = (n: SelectableNode): SelectableNode | null => {
    const children = n.children.map(filter).filter(Boolean) as SelectableNode[];
    return n.name.toLowerCase().includes(q) || children.length > 0 ? { ...n, children } : null;
  };
  return nodes.map(filter).filter(Boolean) as SelectableNode[];
}

type CheckState = 'checked' | 'indeterminate' | 'unchecked';

function getCheckState(node: SelectableNode, selected: Set<string>): CheckState {
  if (selected.has(node.id)) return 'checked';
  const desc = node.children.flatMap(c => descendantIds(c));
  if (desc.some(d => selected.has(d))) return 'indeterminate';
  return 'unchecked';
}

function toggleSelection(node: SelectableNode, selected: Set<string>, flatNodes: OUNode[]): Set<string> {
  const state = getCheckState(node, selected);
  const next = new Set(selected);
  const all = descendantIds(node);
  if (state === 'checked' || state === 'indeterminate') {
    all.forEach(id => next.delete(id));
    ancestorIds(node.id, flatNodes).forEach(id => next.delete(id));
  } else {
    all.forEach(id => next.add(id));
  }
  return next;
}

// ── Mock: site admins per mapped location ─────────────────────────────────────
// These represent site admins derived from sites previously mapped in Site mapping step.

const SITE_ADMINS_BY_NODE: Record<string, { userId: string; siteName: string }> = {
  'loc-france': { userId: 'u-8',  siteName: 'LumApps Paris' },
  'loc-uk':     { userId: 'u-7',  siteName: 'LumApps London' },
  'loc-us':     { userId: 'u-9',  siteName: 'LumApps New York' },
  'loc-japan':  { userId: 'u-13', siteName: 'LumApps Tokyo' },
  'loc-sgp':    { userId: 'u-12', siteName: 'LumApps Singapore' },
};

// ── Avatar helpers ────────────────────────────────────────────────────────────

const AVATAR_COLORS = [
  '#5c6bc0', '#26a69a', '#ef5350', '#ab47bc', '#ffa726',
  '#42a5f5', '#66bb6a', '#ec407a', '#8d6e63', '#78909c',
];

function getInitials(firstName: string, lastName: string): string {
  return `${firstName[0]}${lastName[0]}`.toUpperCase();
}

function avatarColor(userId: string): string {
  const idx = MOCK_USERS.findIndex(u => u.id === userId);
  return AVATAR_COLORS[Math.max(idx, 0) % AVATAR_COLORS.length];
}

// ── Tester types ──────────────────────────────────────────────────────────────

interface TesterEntry {
  key: string;
  userId: string;
  email: string;
  name: string;
  role: 'User' | 'Site admin';
  siteName?: string;
  isAutoIncluded: boolean;
  initials: string;
  color: string;
}

// ── CheckboxInput ─────────────────────────────────────────────────────────────

const CheckboxInput: React.FC<{
  state: CheckState | boolean;
  onChange: () => void;
  style?: React.CSSProperties;
}> = ({ state, onChange, style }) => {
  const ref = useRef<HTMLInputElement>(null);
  const checked = state === 'checked' || state === true;

  useEffect(() => {
    if (ref.current) ref.current.indeterminate = state === 'indeterminate';
  }, [state]);

  return (
    <input
      ref={ref}
      type="checkbox"
      checked={checked}
      onChange={onChange}
      onClick={e => e.stopPropagation()}
      style={{ width: 16, height: 16, cursor: 'pointer', flexShrink: 0, accentColor: 'var(--color-cta)', ...style }}
    />
  );
};

// ── OrgTreeRow ────────────────────────────────────────────────────────────────

const OrgTreeRow: React.FC<{
  node: SelectableNode;
  depth: number;
  selected: Set<string>;
  expanded: Set<string>;
  sitesByNodeId: Record<string, string>;
  onToggleSelect: (id: string) => void;
  onToggleExpand: (id: string) => void;
}> = ({ node, depth, selected, expanded, sitesByNodeId, onToggleSelect, onToggleExpand }) => {
  const [sitesOpen, setSitesOpen] = useState(false);
  const checkState = getCheckState(node, selected);
  const isExpanded = expanded.has(node.id);
  const hasChildren = node.children.length > 0;
  const icon = node.type === 'OrganizationalUnit' ? mdiLan : mdiMapMarker;
  const siteName = sitesByNodeId[node.id];
  const indentLeft = 24 + depth * 20;

  return (
    <>
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 8,
          minHeight: 'var(--row-height)',
          paddingLeft: indentLeft,
          paddingRight: 24,
          borderBottom: sitesOpen ? 'none' : '1px solid var(--color-border)',
          cursor: 'pointer',
        }}
        onClick={() => onToggleSelect(node.id)}
      >
        <button
          type="button"
          style={{
            width: 20, height: 20,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            flexShrink: 0, opacity: hasChildren ? 1 : 0, color: 'var(--color-dark-l3)',
          }}
          onClick={e => { e.stopPropagation(); if (hasChildren) onToggleExpand(node.id); }}
        >
          <MdiIcon path={isExpanded ? mdiChevronDown : mdiChevronRight} size={0.7} />
        </button>

        <CheckboxInput state={checkState} onChange={() => onToggleSelect(node.id)} />

        <MdiIcon path={icon} size={0.75} color="var(--color-dark-l1)" />
        <span style={{ fontSize: 14, color: 'rgba(0,0,0,0.87)' }}>{node.name}</span>

        {siteName && (
          <Chip
            size="s"
            isClickable
            before={<span style={{ display: 'flex', alignItems: 'center' }}><MdiIcon path={mdiApplicationOutline} size={0.55} /></span>}
            after={<span style={{ display: 'flex', alignItems: 'center' }}><MdiIcon path={sitesOpen ? mdiChevronDown : mdiChevronRight} size={0.65} /></span>}
            onClick={(e: React.MouseEvent) => { e.stopPropagation(); setSitesOpen(v => !v); }}
          >
            1 site
          </Chip>
        )}
      </div>

      {siteName && sitesOpen && (
        <div className="site-rows-box">
          <div className="site-sub-row" style={{ paddingLeft: indentLeft + 48 }}>
            <span className="site-sub-row__icon">
              <MdiIcon path={mdiApplicationOutline} size={0.65} color="var(--color-dark-l1)" />
            </span>
            <span className="site-sub-row__name">{siteName}</span>
          </div>
        </div>
      )}

      {isExpanded && node.children.map(child => (
        <OrgTreeRow
          key={child.id}
          node={child}
          depth={depth + 1}
          selected={selected}
          expanded={expanded}
          sitesByNodeId={sitesByNodeId}
          onToggleSelect={onToggleSelect}
          onToggleExpand={onToggleExpand}
        />
      ))}
    </>
  );
};

// ── EmailAutocomplete ─────────────────────────────────────────────────────────

const EmailAutocomplete: React.FC<{
  addedIds: Set<string>;
  onAdd: (user: User) => void;
}> = ({ addedIds, onAdd }) => {
  const [query, setQuery] = useState('');
  const [open, setOpen] = useState(false);
  const wrapRef = useRef<HTMLDivElement>(null);

  const suggestions = useMemo(() => {
    if (!query.trim()) return [];
    const q = query.toLowerCase();
    return MOCK_USERS.filter(u =>
      u.status === 'active' &&
      !addedIds.has(u.id) &&
      (u.email.toLowerCase().includes(q) || `${u.firstName} ${u.lastName}`.toLowerCase().includes(q))
    ).slice(0, 6);
  }, [query, addedIds]);

  useEffect(() => { setOpen(suggestions.length > 0); }, [suggestions]);

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const select = (user: User) => { onAdd(user); setQuery(''); setOpen(false); };

  return (
    <div ref={wrapRef} style={{ position: 'relative' }}>
      <div style={{
        display: 'flex', alignItems: 'center', gap: 8,
        padding: '0 12px', height: 44,
        border: '1px solid rgba(0,0,0,0.20)', borderRadius: 6, background: 'white',
      }}>
        <MdiIcon path={mdiEmailOutline} size={0.8} color="rgba(0,0,0,0.45)" />
        <input
          value={query}
          onChange={e => setQuery(e.target.value)}
          placeholder="Invite by email — press enter to add"
          style={{ flex: 1, border: 'none', outline: 'none', fontSize: 14, background: 'transparent' }}
        />
      </div>

      {open && (
        <div style={{
          position: 'absolute', top: 'calc(100% + 4px)', left: 0, right: 0, zIndex: 50,
          background: 'white', border: '1px solid rgba(0,0,0,0.12)',
          borderRadius: 6, boxShadow: '0 4px 12px rgba(0,0,0,0.12)', overflow: 'hidden',
        }}>
          {suggestions.map(u => (
            <button
              key={u.id}
              type="button"
              onClick={() => select(u)}
              style={{
                display: 'flex', alignItems: 'center', gap: 10,
                width: '100%', padding: '8px 12px', textAlign: 'left',
                borderBottom: '1px solid rgba(0,0,0,0.06)',
              }}
            >
              <div style={{
                width: 32, height: 32, borderRadius: '50%',
                background: avatarColor(u.id), color: 'white',
                fontSize: 11, fontWeight: 600,
                display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
              }}>
                {getInitials(u.firstName, u.lastName)}
              </div>
              <div style={{ minWidth: 0 }}>
                <div style={{ fontSize: 14, fontWeight: 500, color: 'rgba(0,0,0,0.87)' }}>
                  {u.firstName} {u.lastName}
                </div>
                <div style={{ fontSize: 12, color: 'rgba(0,0,0,0.60)' }}>{u.email}</div>
              </div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

// ── TesterCard ────────────────────────────────────────────────────────────────

const TesterCard: React.FC<{ tester: TesterEntry; onRemove: (key: string) => void }> = ({ tester, onRemove }) => (
  <div style={{
    display: 'flex', alignItems: 'center', gap: 12,
    padding: '10px 14px',
    border: '1px solid rgba(0,0,0,0.10)', borderRadius: 6, background: 'white',
  }}>
    <div style={{
      width: 40, height: 40, borderRadius: '50%',
      background: tester.color, color: 'white',
      fontSize: 13, fontWeight: 600,
      display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
    }}>
      {tester.initials}
    </div>

    <div style={{ flex: 1, minWidth: 0 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap', marginBottom: 2 }}>
        <span style={{ fontSize: 14, fontWeight: 600, color: 'rgba(0,0,0,0.87)' }}>{tester.name}</span>
        {tester.isAutoIncluded && (
          <>
            <span style={{ fontSize: 10, fontWeight: 700, color: 'var(--color-cta)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>AUTO</span>
            <span style={{ fontSize: 11, color: 'rgba(0,0,0,0.30)' }}>·</span>
            <span style={{ fontSize: 10, fontWeight: 700, color: 'var(--color-cta)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>SITE ADMIN</span>
          </>
        )}
      </div>
      <div style={{ fontSize: 12, color: 'rgba(0,0,0,0.60)' }}>
        {tester.role}{tester.siteName ? ` · ${tester.siteName}` : ''} · {tester.email}
      </div>
    </div>

    <button
      type="button"
      onClick={() => onRemove(tester.key)}
      style={{ padding: 4, color: 'rgba(0,0,0,0.40)', flexShrink: 0, lineHeight: 0 }}
    >
      <MdiIcon path={mdiClose} size={0.75} />
    </button>
  </div>
);

// ── CreateTestPage ────────────────────────────────────────────────────────────

const CreateTestPage: React.FC<{ onBack: () => void; onLaunch: (run: TestRun) => void; onSaveDraft: (run: TestRun) => void }> = ({ onBack, onLaunch, onSaveDraft }) => {
  const [testName, setTestName]   = useState('');
  const [selected, setSelected]   = useState<Set<string>>(new Set());
  const [expanded, setExpanded]   = useState<Set<string>>(
    new Set(INITIAL_NODES.filter(n => n.parentId === null).map(n => n.id))
  );
  const [searchQuery, setSearch]  = useState('');
  const [testers, setTesters]     = useState<TesterEntry[]>([]);
  const [autoInclude, setAuto]    = useState(false);

  const tree           = useMemo(() => buildSelectableTree(INITIAL_NODES), []);
  const allIds         = useMemo(() => allNodeIds(tree), [tree]);
  const filtered       = useMemo(() => filterTree(tree, searchQuery), [tree, searchQuery]);
  const sitesByNodeId  = useMemo(() =>
    Object.fromEntries(Object.entries(SITE_ADMINS_BY_NODE).map(([id, { siteName }]) => [id, siteName])),
  []);

  const selectAllState: CheckState = useMemo(() => {
    if (selected.size === 0) return 'unchecked';
    if (selected.size === allIds.length) return 'checked';
    return 'indeterminate';
  }, [selected, allIds]);

  // Auto-include site admins whenever selection or autoInclude changes
  useEffect(() => {
    if (!autoInclude) {
      setTesters(prev => prev.filter(t => !t.isAutoIncluded));
      return;
    }
    const newAuto: TesterEntry[] = [];
    Object.entries(SITE_ADMINS_BY_NODE).forEach(([nodeId, { userId, siteName }]) => {
      if (!selected.has(nodeId)) return;
      const user = MOCK_USERS.find(u => u.id === userId);
      if (!user) return;
      newAuto.push({
        key: `auto-${userId}`, userId,
        email: user.email,
        name: `${user.firstName} ${user.lastName}`,
        role: 'Site admin', siteName,
        isAutoIncluded: true,
        initials: getInitials(user.firstName, user.lastName),
        color: avatarColor(userId),
      });
    });
    setTesters(prev => {
      const manual = prev.filter(t => !t.isAutoIncluded);
      const manualIds = new Set(manual.map(t => t.userId));
      return [...newAuto.filter(a => !manualIds.has(a.userId)), ...manual];
    });
  }, [autoInclude, selected]);

  const handleToggleSelect = useCallback((id: string) => {
    const node = findNode(tree, id);
    if (!node) return;
    setSelected(prev => toggleSelection(node, prev, INITIAL_NODES));
  }, [tree]);

  const handleToggleExpand = useCallback((id: string) => {
    setExpanded(prev => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  }, []);

  const handleSelectAll = useCallback(() => {
    setSelected(selectAllState === 'checked' ? new Set() : new Set(allIds));
  }, [selectAllState, allIds]);

  const addedIds = useMemo(() => new Set(testers.map(t => t.userId)), [testers]);

  const handleAddTester = useCallback((user: User) => {
    setTesters(prev => {
      if (prev.some(t => t.userId === user.id)) return prev;
      return [...prev, {
        key: `manual-${user.id}`, userId: user.id,
        email: user.email,
        name: `${user.firstName} ${user.lastName}`,
        role: 'User',
        isAutoIncluded: false,
        initials: getInitials(user.firstName, user.lastName),
        color: avatarColor(user.id),
      }];
    });
  }, []);

  const handleRemoveTester = useCallback((key: string) => {
    setTesters(prev => prev.filter(t => t.key !== key));
  }, []);

  const autoCount = testers.filter(t => t.isAutoIncluded).length;

  const buildRun = (status: 'running' | 'draft'): TestRun => {
    const selectedNames = INITIAL_NODES
      .filter(n => selected.has(n.id))
      .map(n => n.name);
    const today = new Date();
    const startDate = today.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
    return {
      id: `t-${Date.now()}`,
      name: testName.trim() || 'Untitled test',
      scopeCount: selected.size,
      scopeUnits: selectedNames,
      testers: testers.map<Tester>(t => ({ initials: t.initials, color: t.color, name: t.name })),
      thumbsUp: 0,
      thumbsDown: 0,
      startDate: status === 'running' ? startDate : '—',
      status,
      daysLeft: status === 'running' ? 7 : undefined,
    };
  };

  const handleLaunch = () => onLaunch(buildRun('running'));
  const handleSaveDraft = () => onSaveDraft(buildRun('draft'));

  return (
    <div className="main-content" style={{ display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>

      {/* Header */}
      <div className="page-header">
        <div className="page-header-left">
          <button className="dep-crumb" onClick={onBack} aria-label="Back" style={{ color: 'var(--color-dark-l1)' }}>
            <MdiIcon path={mdiArrowLeft} size={0.8} />
          </button>
          <Heading as="h1" typography="headline">Create test</Heading>
        </div>
        <div className="page-header-right">
          <Button emphasis="medium" color="dark" onClick={handleSaveDraft}>Save as draft</Button>
          <Button emphasis="high" color="primary" leftIcon={mdiRocket} onClick={handleLaunch}>Launch test</Button>
        </div>
      </div>

      {/* Test name */}
      <div style={{ padding: '16px 24px', borderBottom: '1px solid rgba(0,0,0,0.12)', flexShrink: 0 }}>
        <div style={{ width: '50%' }}>
          <TextField
            label="Test name"
            value={testName}
            onChange={setTestName}
            placeholder="Give this test a descriptive name"
          />
        </div>
      </div>

      {/* Two panes */}
      <div className="mapping-layout">

        {/* Left: Org unit selector */}
        <div className="pane pane--org">
          <div className="pane-header">
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <CheckboxInput state={selectAllState} onChange={handleSelectAll} />
              <span className="pane-title-text">Selected: {selected.size} units</span>
            </div>
            <div className="pane-header-actions">
              <button
                className="pane-action-btn icon-only"
                onClick={() => setExpanded(new Set(INITIAL_NODES.map(n => n.id)))}
                title="Expand all"
              >
                <MdiIcon path={mdiUnfoldMoreHorizontal} size={0.7} />
              </button>
              <button
                className="pane-action-btn icon-only"
                onClick={() => setExpanded(new Set())}
                title="Collapse all"
              >
                <MdiIcon path={mdiUnfoldLessHorizontal} size={0.7} />
              </button>
            </div>
          </div>

          <div className="pane-search">
            <TextField
              placeholder="Search org units & locations…"
              value={searchQuery}
              onChange={setSearch}
              icon={mdiMagnify}
              clearButtonProps={{ label: 'Clear' }}
            />
          </div>

          <div className="pane-tree" style={{ overflowY: 'auto' }}>
            {filtered.length > 0
              ? filtered.map(node => (
                <OrgTreeRow
                  key={node.id}
                  node={node}
                  depth={0}
                  selected={selected}
                  expanded={expanded}
                  sitesByNodeId={sitesByNodeId}
                  onToggleSelect={handleToggleSelect}
                  onToggleExpand={handleToggleExpand}
                />
              ))
              : (
                <div style={{ padding: 16, color: 'rgba(0,0,0,0.45)', fontSize: 14 }}>
                  {searchQuery ? `No results for "${searchQuery}"` : 'No units'}
                </div>
              )
            }
          </div>
        </div>

        {/* Right: Testers */}
        <div className="pane" style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column' }}>
          <div className="pane-header">
            <span className="pane-title-text">Testers</span>
            <span style={{ fontSize: 13, color: 'rgba(0,0,0,0.60)' }}>
              {testers.length} {testers.length === 1 ? 'tester' : 'testers'}
            </span>
          </div>

          <div style={{ flex: 1, overflowY: 'auto', padding: 16, display: 'flex', flexDirection: 'column', gap: 12 }}>
            <p style={{ fontSize: 13, color: 'rgba(0,0,0,0.60)' }}>
              Anyone in this list will receive a magic link to preview the test scope.
            </p>

            <EmailAutocomplete addedIds={addedIds} onAdd={handleAddTester} />

            {/* Auto-include site admins */}
            <div style={{
              display: 'flex', alignItems: 'center', gap: 10,
              padding: '10px 14px',
              background: 'rgba(36,91,231,0.04)',
              border: '1px solid rgba(36,91,231,0.12)',
              borderRadius: 6,
            }}>
              <input
                type="checkbox"
                checked={autoInclude}
                onChange={e => setAuto(e.target.checked)}
                style={{ width: 16, height: 16, cursor: 'pointer', accentColor: 'var(--color-cta)', flexShrink: 0 }}
              />
              <span style={{ fontSize: 13, color: 'rgba(0,0,0,0.87)' }}>
                <strong>Auto-include site admins</strong> of the selected scope
                {autoCount > 0 && (
                  <span style={{ color: 'rgba(0,0,0,0.55)', marginLeft: 6 }}>
                    · {autoCount} added automatically
                  </span>
                )}
              </span>
            </div>

            {testers.map(t => (
              <TesterCard key={t.key} tester={t} onRemove={handleRemoveTester} />
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default CreateTestPage;
